import { Button } from "@/components/ui/button";
import {
  FileText,
  Mail,
  MailOpen,
  MousePointerClick,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Star,
  Sparkles,
  Loader2,
  ExternalLink,
  Trash2,
  Send,
  BellRing,
  Printer,
  Clock,
  XCircle,
  Video,
} from "lucide-react";
import { scoreColor } from "./scoreColor";

export default function ResumeRow({
  resume: r,
  index,
  expanded,
  onToggle,
  onView,
  onRescore,
  onDelete,
  onOpenInterview,
  onSendReminder,
  onCancelSchedule,
  onPrintSummary,
  rescoring,
  reminding,
  cancellingSchedule,
  maxReminders = 2,
  topScoreFloor,
}) {
  const i = index;
  const isOpen = expanded;
  const sentCount =
    r.reminders_sent_count ??
    (r.reminder_sent_at ? 1 : 0);
  const canRemind = Boolean(
    r.interview_id &&
      r.email_sent_at &&
      maxReminders > 0 &&
      sentCount < maxReminders
  );

  return (
    <li
      data-testid={`resume-row-${i}`}
      className={`border-b border-slate-100 last:border-0 ${
        topScoreFloor != null &&
        typeof r.score === "number" &&
        r.score >= topScoreFloor
          ? "bg-emerald-50/60"
          : ""
      }`}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-start gap-4 p-5 text-left hover:bg-slate-50"
        data-testid={`resume-toggle-${i}`}
      >
        <div className="flex h-10 w-10 flex-none items-center justify-center bg-slate-900 font-heading text-base font-semibold text-white">
          {i + 1}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate font-heading text-base font-medium">
              {r.candidate_name || r.filename}
            </div>
            {(() => {
              const srcRaw = (r.source || "uploaded").toLowerCase();
              // Collapse legacy aliases into the canonical bucket
              const src =
                srcRaw === "upload" || srcRaw === "recruiter_upload"
                  ? "uploaded"
                  : srcRaw;
              const meta = {
                online_application: {
                  label: "Online App",
                  cls: "border-amber-300 bg-amber-50 text-amber-900",
                },
                online_application_with_resume: {
                  label: "Form + Résumé",
                  cls: "border-teal-200 bg-teal-50 text-teal-700",
                },
                uploaded: {
                  label: "Uploaded",
                  cls: "border-slate-200 bg-slate-50 text-slate-700",
                },
                email_forward: {
                  label: "Email",
                  cls: "border-violet-200 bg-violet-50 text-violet-700",
                },
                careers_page: {
                  label: "Careers page",
                  cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
                },
                public_apply: {
                  label: "Public apply",
                  cls: "border-sky-200 bg-sky-50 text-sky-700",
                },
              }[src] || {
                label: src,
                cls: "border-slate-200 bg-slate-50 text-slate-600",
              };
              return (
                <span
                  data-testid={`resume-source-pill-${i}`}
                  title={`Source: ${src}`}
                  className={`inline-flex items-center border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}
                >
                  {meta.label}
                </span>
              );
            })()}
            <div
              data-testid={`resume-score-${i}`}
              className={`shrink-0 border px-2 py-0.5 text-xs font-bold ${scoreColor(
                r.score
              )}`}
            >
              {r.score}/100
            </div>
            {typeof r.interview_weighted_score === "number" && (
              <div
                data-testid={`resume-weighted-score-${i}`}
                title="Interview score weighted by this job's scorecard rubric"
                className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700"
              >
                Weighted · {r.interview_weighted_score.toFixed(1)}/10
              </div>
            )}
            {r.analysis_error && (
              <div
                data-testid={`resume-analysis-error-${i}`}
                title="AI analysis failed — try re-scoring"
                className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700"
              >
                <AlertTriangle className="h-3 w-3" />
                Analysis incomplete
              </div>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {r.candidate_email && <span>{r.candidate_email}</span>}
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {r.filename}
            </span>
            {r.application_status === "scheduled" && r.scheduled_send_at && (
              <span
                data-testid={`resume-scheduled-badge-${i}`}
                title={`Will email ${new Date(r.scheduled_send_at).toLocaleString()}`}
                className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700"
              >
                <Clock className="h-3 w-3" />
                Scheduled · {new Date(r.scheduled_send_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            )}
            {r.interview_id && r.application_status !== "scheduled" && (
              <span
                data-testid={`resume-interview-badge-${i}`}
                className="inline-flex items-center gap-1 border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-700"
              >
                <Mail className="h-3 w-3" />
                Invited
              </span>
            )}
            {r.email_opened_at && (
              <span
                data-testid={`resume-opened-badge-${i}`}
                title={`Opened ${new Date(r.email_opened_at).toLocaleString()}`}
                className="inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700"
              >
                <MailOpen className="h-3 w-3" />
                Opened
              </span>
            )}
            {r.email_clicked_at && (
              <span
                data-testid={`resume-clicked-badge-${i}`}
                title={`Clicked ${new Date(r.email_clicked_at).toLocaleString()}`}
                className="inline-flex items-center gap-1 border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700"
              >
                <MousePointerClick className="h-3 w-3" />
                Clicked
              </span>
            )}
            {r.reminder_sent_at && (
              <span
                data-testid={`resume-reminded-badge-${i}`}
                title={`Last reminded ${new Date(
                  r.last_reminder_sent_at || r.reminder_sent_at
                ).toLocaleString()}`}
                className="inline-flex items-center gap-1 border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700"
              >
                <BellRing className="h-3 w-3" />
                Reminded
                {(r.reminders_sent_count ?? 1) > 1 && (
                  <span data-testid={`resume-reminded-count-${i}`}>
                    {" "}· {r.reminders_sent_count}
                  </span>
                )}
              </span>
            )}
          </div>
          {r.summary && !isOpen && (
            <p className="mt-2 line-clamp-2 text-sm text-slate-600">
              {r.summary}
            </p>
          )}
        </div>
        <div className="hidden flex-none text-slate-400 sm:block">
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-slate-100 bg-slate-50 p-5">
          {r.summary && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-900">
                Summary
              </div>
              <p className="mt-1 text-sm text-slate-700">{r.summary}</p>
            </div>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Top skills
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(r.top_skills || []).map((s) => (
                  <span
                    key={s}
                    className="border border-slate-300 bg-white px-2 py-0.5 text-xs"
                  >
                    {s}
                  </span>
                ))}
                {(!r.top_skills || r.top_skills.length === 0) && (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">
                <Star className="h-3 w-3 fill-current" /> Strengths
              </div>
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {(r.strengths || []).map((s, k) => (
                  <li key={k}>· {s}</li>
                ))}
                {(!r.strengths || r.strengths.length === 0) && (
                  <li className="text-slate-400">—</li>
                )}
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">
                Gaps
              </div>
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {(r.gaps || []).map((g, k) => (
                  <li key={k}>· {g}</li>
                ))}
                {(!r.gaps || r.gaps.length === 0) && (
                  <li className="text-slate-400">—</li>
                )}
              </ul>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
            <Button
              size="sm"
              variant="outline"
              data-testid={`resume-view-btn-${i}`}
              onClick={() => onView(r)}
              className="rounded-sm border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
            >
              <ExternalLink className="mr-2 h-3 w-3" />
              View Resume
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid={`resume-start-1on1-btn-${i}`}
              onClick={async () => {
                if (!r.job_id) return;
                try {
                  const { data } = await (await import("@/lib/api")).default.post(
                    `/jobs/${r.job_id}/live-rooms`,
                    {
                      candidate_name: r.candidate_name || "",
                      candidate_email: r.candidate_email || "",
                    }
                  );
                  if (data?.id) {
                    window.open(
                      `/jobs/${r.job_id}/live/${data.id}`,
                      "_blank"
                    );
                  }
                } catch (e) {
                  // Best-effort — show whatever the backend said
                  alert(
                    e?.response?.data?.detail ||
                      "Could not start a 1:1 room"
                  );
                }
              }}
              className="rounded-sm border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              <Video className="mr-2 h-3 w-3" />
              Start 1:1 Interview
            </Button>
            {r.source === "online_application" && (
              <Button
                size="sm"
                variant="outline"
                data-testid={`resume-view-app-btn-${i}`}
                onClick={() => onView(r)}
                title="Online application submission"
                className="rounded-sm border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                style={{ borderColor: "#a16207", color: "#78350f" }}
              >
                <FileText className="mr-2 h-3 w-3" />
                View Online Application
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              data-testid={`resume-rescore-btn-${i}`}
              onClick={() => onRescore(r)}
              disabled={rescoring === r.id}
              className="rounded-sm border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            >
              {rescoring === r.id ? (
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-3 w-3" />
              )}
              Re-score with AI
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid={`resume-print-btn-${i}`}
              onClick={() => onPrintSummary(r)}
              className="rounded-sm border-slate-300 bg-white"
            >
              <Printer className="mr-2 h-3 w-3" />
              Print Summary
            </Button>
            {r.interview_id && (
              <Button
                size="sm"
                variant="outline"
                data-testid={`resume-open-interview-btn-${i}`}
                onClick={() => onOpenInterview(r)}
                className="rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Mail className="mr-2 h-3 w-3" />
                Open interview
              </Button>
            )}
            {canRemind && (
              <Button
                size="sm"
                variant="outline"
                data-testid={`resume-remind-btn-${i}`}
                onClick={() => onSendReminder(r)}
                disabled={reminding === r.id}
                className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {reminding === r.id ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : (
                  <Send className="mr-2 h-3 w-3" />
                )}
                Send reminder
              </Button>
            )}
            {r.application_status === "scheduled" && onCancelSchedule && (
              <Button
                size="sm"
                variant="outline"
                data-testid={`resume-cancel-schedule-btn-${i}`}
                onClick={() => onCancelSchedule(r)}
                disabled={cancellingSchedule === r.id}
                className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {cancellingSchedule === r.id ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-3 w-3" />
                )}
                Cancel schedule
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              data-testid={`resume-delete-btn-${i}`}
              onClick={() => onDelete(r)}
              className="ml-auto rounded-sm border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="mr-2 h-3 w-3" />
              Delete
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
